import React, {ComponentType, useMemo} from 'react';
import ContactInfoForm from 'shared/account-settings/components/ContactInfoForm';
import AccountSettings, {
  AccountSettingsTabComponent,
  Tab,
} from 'src/components/AccountSettings/AccountSettings';
import {SharedAccountSettings} from 'src/components/AccountSettings/AccountSettingsContext';
import {ACCOUNT_SETTING_ROUTES} from 'src/components/AccountSettings/utils/utils';
import {FeatureFlags} from 'src/services/FeatureFlagService';
import {withFeatureFlags} from '../../../hooks/useFeatureFlags';
import {COLLEGE_PORTAL_USER_FIELDS} from '../../../redux/college-portal/actions/college-portal-user';
import CRUDService from '../../../services/CRUDService';
import ManageDataSharing from '../manage-data-sharing';
import ManageEmailMessaging from '../manage-email-messaging/manage-email-messaging';
import SpotAwardFileUploadForm from '../spot-award-file-upload-form';
import {getNotificationSettings} from './notification-settings';
import useBrandName from 'src/hooks/use-brand-name';
import ProfileForm from 'src/views/college-portal/account-settings/profile-form';

export type CollegePortalAccountSettings = SharedAccountSettings & {
  _id: string;
  title: string;
  firstName: string;
  lastName: string;
  department: string;
  role: string;
};

const SettingsService = new CRUDService<CollegePortalAccountSettings>('/v1/college-portal-users');

const BASE_TABS: Tab<CollegePortalAccountSettings>[] = [
  {
    ...ACCOUNT_SETTING_ROUTES.profile,
    component: ProfileForm,
  },
  {
    ...ACCOUNT_SETTING_ROUTES.contact,
    component: function ContactInfoTab({
      settings,
      saveSettings,
      setNavigationAnnouncement,
      ...props
    }: AccountSettingsTabComponent<CollegePortalAccountSettings>) {
      return (
        <ContactInfoForm
          initialValues={settings}
          // @ts-expect-error
          onSubmit={saveSettings}
          setNavigationAnnouncement={setNavigationAnnouncement}
          {...props}
        />
      );
    },
  },
  {
    ...ACCOUNT_SETTING_ROUTES['manage-data-sharing'],
    component: ManageDataSharing,
  },
];

const fetchAccountSettings = (): Promise<CollegePortalAccountSettings> =>
  SettingsService.show('me', {
    fields: `${COLLEGE_PORTAL_USER_FIELDS.join(',')},notificationSettings`,
  });

const CollegePortalAccountSettings = ({
  featureFlags,
}: {
  featureFlags: {
    bulkSpotAwardFormVisible: boolean;
    manageEmailAliases: boolean;
    canViewManageEmailMessaging: boolean;
  };
}) => {
  const brandName = useBrandName();
  const tabs = useMemo(() => {
    const result = [...BASE_TABS];

    if (featureFlags.bulkSpotAwardFormVisible) {
      result.push({
        ...ACCOUNT_SETTING_ROUTES['spot-awards'],
        component: SpotAwardFileUploadForm,
      });
    }

    if (featureFlags.canViewManageEmailMessaging || featureFlags.manageEmailAliases) {
      result.push({
        ...ACCOUNT_SETTING_ROUTES['manage-email-messaging'],
        ...(featureFlags.canViewManageEmailMessaging && {
          label: 'Email Messaging Management',
          path: '/account-settings/manage-email-messaging',
        }),
        component: ManageEmailMessaging,
      });
    }

    return result;
  }, [
    featureFlags.bulkSpotAwardFormVisible,
    featureFlags.canViewManageEmailMessaging,
    featureFlags.manageEmailAliases,
  ]);

  return (
    <AccountSettings<CollegePortalAccountSettings>
      tabs={tabs}
      fetchAccountSettings={fetchAccountSettings}
      availableNotificationSettings={getNotificationSettings(brandName)}
      saveSettings={(newSettings): Promise<CollegePortalAccountSettings> =>
        SettingsService.update('me', newSettings, {fields: COLLEGE_PORTAL_USER_FIELDS.join(',')})
      }
    />
  );
};

export default withFeatureFlags(
  CollegePortalAccountSettings as ComponentType<{featureFlags: FeatureFlags}>,
  ['bulkSpotAwardFormVisible', 'manageEmailAliases', 'canViewManageEmailMessaging'],
  false
);
